import clsx from "clsx";
import type { ReactNode } from "react";
import { points, shortName } from "../lib/format";
import {
  parseFormation,
  POSITION_NAMES,
  type Position,
} from "../lib/positions";

/**
 * El campo de juego: el elemento firma de la sección 8.4.
 *
 * SOLO CSS
 *   Ni una imagen, ni un SVG, ni un icono. Las líneas de cal son bordes, el círculo central y los
 *   arcos son `border-radius`, y las rayas de siega un degradado repetido. Todo vive en
 *   `styles.css` bajo `.pitch__*`. La razón no es purismo: una imagen de campo se ve mal en cada
 *   densidad de pantalla distinta y hay que servirla; esto escala solo y no pesa nada.
 *
 * CÓMO SE COLOCAN LAS ONCE FICHAS
 *   La formación (`"4-3-3"`) da las cuatro líneas, y los jugadores se reparten **agrupados por su
 *   posición**, no por el orden en que lleguen. Así un XI que venga ordenado de otra manera —o al
 *   que le falte un dato— sigue apareciendo en su sitio en vez de con el portero en la punta.
 *   Si sobran jugadores en una posición, los de más se quedan fuera: el campo tiene once huecos.
 *
 * ACCESIBILIDAD
 *   Un dibujo no se lee. El once es una lista de verdad (`role="list"`), y cada ficha lleva su
 *   etiqueta completa: dorsal, nombre, posición, puntos esperados y, si lo tiene, el estado. El
 *   estado nunca se distingue solo por color: quien sale lleva el trazo discontinuo y todos lo
 *   llevan escrito.
 */

export type ManState = "normal" | "out" | "in" | "warn";

const MAN_STATE_LABELS: Record<ManState, string | null> = {
  normal: null,
  out: "sale",
  in: "entra",
  warn: "con aviso",
};

const MAN_STATE_CLASSES: Record<ManState, string | null> = {
  normal: null,
  out: "man--out",
  in: "man--in",
  warn: "man--warn",
};

export interface PitchPlayer {
  id: number;
  name: string;
  position: Position;
  /** Dorsal sobre la ficha. Sin dorsal se usan las iniciales del nombre. */
  shirt?: number | string | null;
  /** Puntos esperados de la jornada; `null` los oculta. */
  expected?: number | null;
  state?: ManState;
  /** Motivo del aviso, cuando lo hay: *No juega esta jornada*, *Duda*. */
  warning?: string | null;
}

export interface PitchProps {
  /** Formación tal cual la da el servidor: `"4-3-3"`. */
  formation: string;
  players: PitchPlayer[];
  size?: "full" | "mini";
  /** A sangre: sin esquinas redondeadas, para el campo que abre Inicio. */
  bleed?: boolean;
  /** Rótulo de televisión sobre el degradado inferior. */
  bug?: ReactNode;
  className?: string;
}

/** Reparte a los jugadores por línea respetando su posición. */
function layOut(
  formation: string,
  players: PitchPlayer[],
): { position: Position; players: PitchPlayer[] }[] | null {
  const leida = parseFormation(formation);
  if (!leida) return null;

  const restantes = new Map<Position, PitchPlayer[]>();
  for (const player of players) {
    const cola = restantes.get(player.position) ?? [];
    cola.push(player);
    restantes.set(player.position, cola);
  }

  return leida.lines.map((line) => ({
    position: line.position,
    players: (restantes.get(line.position) ?? []).slice(0, line.count),
  }));
}

function manLabel(player: PitchPlayer): string {
  const partes = [player.name, POSITION_NAMES[player.position]];
  if (player.expected !== undefined && player.expected !== null) {
    partes.push(`${points(player.expected)} puntos esperados`);
  }
  const estado = MAN_STATE_LABELS[player.state ?? "normal"];
  if (estado) partes.push(estado);
  if (player.warning) partes.push(player.warning);
  return partes.join(", ");
}

function Man({ player }: { player: PitchPlayer }) {
  const estado = player.state ?? "normal";
  const dorsal =
    player.shirt === undefined || player.shirt === null
      ? player.name.trim().slice(0, 2).toUpperCase()
      : String(player.shirt);

  return (
    <div
      role="listitem"
      aria-label={manLabel(player)}
      title={player.warning ?? undefined}
      className={clsx("man", MAN_STATE_CLASSES[estado])}
    >
      <span aria-hidden="true" className="man__shirt">
        {dorsal}
      </span>
      <span aria-hidden="true" className="man__name">
        {shortName(player.name)}
      </span>
      {player.expected !== undefined && player.expected !== null ? (
        <span aria-hidden="true" className="man__points">
          {points(player.expected)}
        </span>
      ) : null}
    </div>
  );
}

/** Las líneas de cal. Puro adorno para quien lee la pantalla: no aporta nada al árbol. */
function PitchLines() {
  return (
    <div aria-hidden="true" className="pitch__lines">
      <div className="pitch__border" />
      <div className="pitch__halfway" />
      <div className="pitch__circle" />
      <div className="pitch__spot" />
      <div className="pitch__pen pitch__pen--top" />
      <div className="pitch__six pitch__six--top" />
      <div className="pitch__pspot pitch__pspot--top" />
      <div className="pitch__arc pitch__arc--top" />
      <div className="pitch__goal pitch__goal--top" />
      <div className="pitch__pen pitch__pen--bottom" />
      <div className="pitch__six pitch__six--bottom" />
      <div className="pitch__pspot pitch__pspot--bottom" />
      <div className="pitch__arc pitch__arc--bottom" />
      <div className="pitch__goal pitch__goal--bottom" />
      <div className="pitch__corner pitch__corner--tl" />
      <div className="pitch__corner pitch__corner--tr" />
      <div className="pitch__corner pitch__corner--bl" />
      <div className="pitch__corner pitch__corner--br" />
    </div>
  );
}

export function Pitch({
  formation,
  players,
  size = "full",
  bleed = false,
  bug,
  className,
}: PitchProps) {
  const lineas = layOut(formation, players);

  return (
    <div
      className={clsx(
        "pitch",
        size === "mini" && "pitch--mini",
        bleed && "pitch--bleed",
        className,
      )}
      data-formation={formation}
    >
      <PitchLines />

      {lineas ? (
        <div
          role="list"
          aria-label={`Once inicial en ${formation}`}
          className="pitch__xi"
        >
          {/* De arriba abajo: primero la delantera, y el portero al pie de su portería. */}
          {[...lineas].reverse().map((linea) => (
            <div role="none" key={linea.position} className="pitch__line">
              {linea.players.map((player) => (
                <Man key={player.id} player={player} />
              ))}
            </div>
          ))}
        </div>
      ) : null}

      {bug ? <div className="pitch__bug">{bug}</div> : null}
    </div>
  );
}
